import Vue from "vue";
import VueRouter from "vue-router";
import Container from "@/views/container/Container";
import AuthContainer from "@/views/auth/AuthContainer";
import DefaultHeader from "@/components/DefaultHeader";
import { captureVerifyToken } from "@/helpers/onboard";
import authMiddleware from "./authMiddleware";

captureVerifyToken();

const routes = [
  {
    path: "/start",
    name: "Start",
    component: () => import("@/views/onboard/Onboard")
  },
  {
    path: "/pay-invoice",
    name: "Pay Invoice",
    component: () => import("@/views/payments/PayInvoice")
  },
  {
    path: "/payment-summary",
    name: "Payment Summary",
    component: () => import("@/views/payments/PaymentSummary")
  },
  {
    path: "/auth",
    name: "Authentication",
    component: AuthContainer,
    meta: {
      guest: true
    },
    children: [
      {
        path: "login",
        name: "Login",
        component: () => import("@/views/auth/Login")
      },
      {
        path: "register",
        name: "Register",
        redirect: "/start"
      },
      {
        path: "reset-password",
        component: () => import("@/views/auth/ResetPassword")
      },
      {
        path: "email-verification",
        component: () => import("@/views/auth/EmailVerification")
      },
      {
        path: "action",
        component: () => import("@/views/auth/EmailVerification")
      },
      {
        name: 'Setup',
        path: 'setup',
        component: () => import("@/views/auth/Setup"),
        meta: {
          requiresAuth: true
        }
      }
    ]
  },
  {
    path: "/",
    component: Container,
    redirect: '/bookings',
    meta: {
      requiresAuth: true
    },
    children: [
      {
        path: "/dashboard",
        redirect: "/bookings"
        // name: "Dashboard",
        // components: {
        //   header: DashboardHeader,
        //   default: () => import("@/views/dashboard/Dashboard")
        // },
        // meta: {
        //   resource: "DASHBOARD"
        // }
      },
      {
        path: "/activity",
        name: "Activity",
        components: {
          header: DefaultHeader,
          default: () => import("@/views/activity/Activity")
        },
        meta: {
          resource: "ACTIVITY"
        }
      },
      {
        path: "/account",
        name: "Account",
        components: {
          header: DefaultHeader,
          default: () => import("@/views/user/Account")
        },
        meta: {
          resource: "ACCOUNT"
        }
      },
      {
        path: "/user/subscription",
        name: "Subscription",
        components: {
          header: DefaultHeader,
          default: () => import("@/views/user/Subscription")
        },
        meta: {
          resource: "ACCOUNT"
        }
      },
      {
        path: "/settings/business-details",
        name: "Business Details",
        components: {
          header: DefaultHeader,
          default: () => import("@/views/settings/SettingsBusinessDetails")
        },
        meta: {
          hideFooter: true
        }
      },
      {
        path: "/settings/accounting",
        name: "Accounting Settings",
        components: {
          header: DefaultHeader,
          default: () => import("@/views/settings/SettingsAccounting")
        },
        meta: {
          action: "read",
          resource: "ACCOUNTING",
          hideFooter: true
        }
      },
      {
        path: "/settings/accounting/payments",
        name: "Payments Settings",
        components: {
          header: DefaultHeader,
          default: () => import("@/views/settings/SettingsPayments")
        },
        meta: {
          action: "read",
          resource: "ACCOUNTING",
          hideFooter: true
        }
      },
      {
        path: "/settings/rostering",
        name: "Rostering Settings",
        components: {
          header: DefaultHeader,
          default: () => import("@/views/settings/SettingsRostering")
        },
        meta: {
          action: "read",
          resource: "ROSTER",
          hideFooter: true
        }
      },
      {
        path: "/settings/shopfront",
        name: "Shopfront",
        components: {
          header: DefaultHeader,
          default: () => import("@/views/settings/SettingsShopfront")
        },
        meta: {
          action: "read",
          resource: "BOOKINGS",
          hideFooter: true
        }
      },
      {
        path: "/customers",
        name: "Customers",
        components: {
          header: DefaultHeader,
          default: () => import("@/views/customers/CustomerList")
        },
        meta: {
          resource: "CUSTOMERS"
        }
      },
      {
        path: "/customers/add",
        name: "Add Customer",
        components: {
          header: DefaultHeader,
          default: () => import("@/views/customers/CustomerAdd")
        },
        meta: {
          action: "create",
          resource: "CUSTOMERS"
        }
      },
      {
        path: "/customers/edit/:customerId",
        name: "Edit Customer",
        components: {
          header: DefaultHeader,
          default: () => import("@/views/customers/CustomerEdit")
        },
        meta: {
          action: "update",
          resource: "CUSTOMERS"
        }
      },
      {
        path: "/customers/view/:customerId",
        name: "View Customer",
        components: {
          header: DefaultHeader,
          default: () => import("@/views/customers/CustomerView")
        },
        meta: {
          action: "read",
          resource: "CUSTOMERS"
        }
      },
      {
        path: "/invoices",
        name: "Invoices",
        components: {
          header: DefaultHeader,
          default: () => import("@/views/accounting/invoices/InvoicesList")
        },
        meta: {
          resource: "INVOICES"
        }
      },
      {
        path: "/invoices/add",
        name: "Add Invoice",
        components: {
          header: DefaultHeader,
          default: () => import("@/views/accounting/invoices/InvoiceAdd")
        },
        meta: {
          action: "create",
          resource: "INVOICES"
        }
      },
      {
        path: "/invoices/view/:id",
        name: "View Invoice",
        components: {
          header: DefaultHeader,
          default: () => import("@/views/accounting/invoices/InvoiceView")
        },
        meta: {
          resource: "INVOICES"
        }
      },
      {
        path: "/recurring-invoices",
        name: "Recurring Invoices",
        components: {
          header: DefaultHeader,
          default: () =>
            import("@/views/accounting/recurringInvoices/RecurringInvoicesList")
        },
        meta: {
          resource: "INVOICES"
        }
      },
      {
        path: "/recurring-invoices/add",
        name: "Add Recurring Invoice",
        components: {
          header: DefaultHeader,
          default: () =>
            import("@/views/accounting/recurringInvoices/RecurringInvoiceAdd")
        },
        meta: {
          action: "create",
          resource: "RECURRING_INVOICES"
        }
      },
      {
        path: "/recurring-invoices/view/:id",
        name: "View Recurring Invoice",
        components: {
          header: DefaultHeader,
          default: () =>
            import("@/views/accounting/recurringInvoices/RecurringInvoiceView")
        },
        meta: {
          resource: "RECURRING_INVOICES"
        }
      },
      {
        path: "/credit-notes",
        name: "Credit Notes",
        components: {
          header: DefaultHeader,
          default: () => import("@/views/accounting/creditNotes/CreditNotes")
        },
        meta: {
          resource: "CREDIT_NOTES"
        }
      },
      {
        path: "/credit-notes/generate/:invoiceId",
        name: "Generate Credit Note",
        components: {
          header: DefaultHeader,
          default: () =>
            import("@/views/accounting/creditNotes/CreditNoteGenerate")
        },
        meta: {
          action: "create",
          resource: "CREDIT_NOTES"
        }
      },
      {
        path: "/credit-notes/view/:id",
        name: "View Credit Note",
        components: {
          header: DefaultHeader,
          default: () => import("@/views/accounting/creditNotes/CreditNoteView")
        },
        meta: {
          resource: "CREDIT_NOTES"
        }
      },
      {
        path: "/accounting/bookings-v2",
        name: "Bookings v2 (Beta)",
        components: {
          header: DefaultHeader,
          default: () => import("@/views/bookingsV2/BookingsV2")
        },
        meta: {
          resource: "BOOKINGS",
          hideFooter: true
        }
      },
      {
        path: "/bookings",
        redirect: "/bookings/overview"
      },
      {
        path: "/bookings/overview",
        name: "Bookings",
        components: {
          header: DefaultHeader,
          default: () => import("@/views/bookings/Bookings")
        },
        meta: {
          resource: "BOOKINGS"
        }
      },
      {
        path: "/bookings/overview/:date",
        name: "Bookings Overview",
        components: {
          header: DefaultHeader,
          default: () => import("@/views/bookings/Bookings")
        },
        meta: {
          resource: "BOOKINGS"
        }
      },
      {
        path: "/bookings/add",
        name: "Add Booking",
        components: {
          header: DefaultHeader,
          default: () => import("@/views/bookings/BookingAdd")
        },
        meta: {
          resource: "BOOKINGS",
          action: "CREATE"
        }
      },
      {
        path: "/bookings/search",
        name: "Search Bookings",
        components: {
          header: DefaultHeader,
          default: () => import("@/views/bookings/BookingsSearchTable")
        },
        meta: {
          resource: "BOOKINGS",
          action: "READ"
        }
      },
      {
        path: "/management/employees",
        name: "Employees",
        components: {
          header: DefaultHeader,
          default: () => import("@/views/management/employees/Employees.vue")
        },
        meta: {
          resource: "EMPLOYEES"
        }
      },
      {
        path: "/management/employees/add",
        name: "Add Employee",
        components: {
          header: DefaultHeader,
          default: () => import("@/views/management/employees/EmployeeAdd.vue")
        },
        meta: {
          resource: "EMPLOYEES",
          action: "create"
        }
      },
      {
        path: "/management/employees/edit/:id",
        name: "Edit Employee",
        components: {
          header: DefaultHeader,
          default: () => import("@/views/management/employees/EmployeeEdit.vue")
        },
        meta: {
          resource: "EMPLOYEES",
          action: "update"
        }
      },
      {
        path: "/management/roster",
        redirect: "/management/roster/calendar",
        meta: {
          resource: "ROSTER"
        }
      },
      {
        path: "/management/roster/calendar",
        name: "Roster",
        components: {
          header: DefaultHeader,
          default: () => import("@/views/management/roster/Roster")
        },
        meta: {
          resource: "ROSTER"
        }
      },
      {
        path: "/management/roster/calendar/:view/:date",
        name: "Roster Calendar",
        components: {
          header: DefaultHeader,
          default: () => import("@/views/management/roster/Roster")
        },
        meta: {
          resource: "ROSTER"
        }
      },
      {
        path: "/management/roster-v2",
        name: "Roster v2 (Beta)",
        components: {
          header: DefaultHeader,
          default: () => import("@/views/management/rosterV2/RosterV2")
        },
        meta: {
          resource: "ROSTER",
          hideFooter: true
        }
      },
      {
        path: "/management/services",
        name: "Services",
        components: {
          header: DefaultHeader,
          default: () => import("@/views/management/services/Services.vue")
        },
        meta: {
          resource: "SERVICES"
        }
      },
      {
        path: "/management/services/add",
        name: "Add Service",
        components: {
          header: DefaultHeader,
          default: () => import("@/views/management/services/ServiceAdd.vue")
        },
        meta: {
          resource: "SERVICES",
          action: "create"
        }
      },
      {
        path: "/management/services/edit/:id",
        name: "Edit Service",
        components: {
          header: DefaultHeader,
          default: () => import("@/views/management/services/ServiceEdit.vue")
        },
        meta: {
          resource: "SERVICES",
          action: "update"
        }
      }
    ]
  },
  {
    path: "/email-verification",
    redirect: to => ({ path: "/auth/email-verification", query: to.query })
  },
  {
    path: "/reset-password",
    redirect: to => ({ path: "/auth/reset-password", query: to.query })
  },
  {
    path: "*",
    redirect: "/"
  }
];

Vue.use(VueRouter);

const router = new VueRouter({
  routes,
  linkActiveClass: "active",
  mode: "history",
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  }
});

router.beforeEach((to, from, next) => {
  captureVerifyToken(to);
  return authMiddleware(to, from, next);
});

export default router;
